import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Zone 1 — Bloc portrait vertical, placé SOUS le compte à rebours JP 2027
 * dans la sidebar droite de la home. Rotation automatique si plusieurs items.
 */
export function HighlightBlockVertical() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get("/home/highlights", { params: { zone: "sidebar_vertical" } })
      .then((res) => {
        if (!alive) return;
        setItems(res.data?.items || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => {
      setCurrent((c) => (c + 1) % items.length);
    }, 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const item = items[current];
  const link = item.cta_url || (item.linked_entity_slug ? buildLinkedUrl(item) : "#");

  return (
    <div className="space-y-2" data-testid="home-highlight-sidebar">
      <Card className="overflow-hidden bg-background/95 backdrop-blur-sm shadow-md border-[hsl(var(--aito-lagoon))]/20">
        {item.image_url && (
          <Link to={link} className="block relative aspect-[3/4] bg-muted overflow-hidden group">
            <img
              src={item.image_url}
              alt={item.title || "Mise en avant"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            {item.badge && (
              <Badge
                variant="secondary"
                className="absolute top-2 right-2 text-[9px] bg-background/85 backdrop-blur-sm uppercase tracking-wide"
                data-testid="highlight-badge"
              >
                {item.badge === "sponsorise" ? "Sponsorisé" : "Partenaire"}
              </Badge>
            )}
          </Link>
        )}
        <div className="p-3 space-y-2">
          {item.title && (
            <h4 className="font-display font-semibold text-sm leading-tight">{item.title}</h4>
          )}
          {item.subtitle && (
            <p className="text-xs text-muted-foreground line-clamp-2">{item.subtitle}</p>
          )}
          <Link to={link}>
            <Button size="sm" variant="outline" className="w-full text-xs h-8 border-[hsl(var(--aito-lagoon))]/40 hover:bg-[hsl(var(--aito-lagoon))] hover:text-black">
              {item.cta_label || "Découvrir"}
              <ExternalLink className="h-3 w-3 ml-1.5" />
            </Button>
          </Link>
          {items.length > 1 && (
            <div className="flex items-center justify-center gap-1 pt-1">
              {items.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Aller au highlight ${i + 1}`}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${i === current ? "w-4 bg-[hsl(var(--aito-lagoon))]" : "w-1.5 bg-muted-foreground/30"}`}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
      {/* CTA discret — Mettre en avant */}
      <Link
        to="/contact"
        className="block text-[10px] text-center text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] transition-colors"
        data-testid="highlight-sidebar-cta-contact"
      >
        Mettre en avant votre activité sur AITO.PF →
      </Link>
    </div>
  );
}

function buildLinkedUrl(item) {
  const kind = item.content_kind;
  const slug = item.linked_entity_slug;
  if (!slug) return "#";
  const map = {
    athlete: `/athletes/${slug}`,
    coach: `/coaches/${slug}`,
    club: `/clubs/${slug}`,
    federation: `/federations/${slug}`,
    directory_entry: `/annuaire/boutiques-services/${slug}`,
    campaign: `/campagnes/${slug}`,
    news: `/actualites/${slug}`,
    event: `/calendrier`,
  };
  return map[kind] || "#";
}

export default HighlightBlockVertical;
